<script lang="ts">
import $ from 'jquery';
import {defineComponent} from 'vue';
import {AllSpiceJsonLoader} from '../features/allspicejson.ts';
import {cssForVariant} from './svg_utils.ts';
import {vueInstance} from '../domutils.ts';
import type {ProjectData} from '../allspice_app/types/project-data.ts';
import type {Container} from '../container.ts';

class Variant {
  id: string;
  name: string;

  constructor(id: string, name: string) {
    this.id = id;
    this.name = name;
  }
}

export default defineComponent({
  name: 'VariantDropdown',

  inject: {
    untypedContainer: {from: 'container'},
  },

  props: {
    isDiff: {
      type: Boolean,
      required: false,
      default: true,
    },
    headRepoOwner: {
      type: String,
      required: true,
      default: '',
    },
    headRepoName: {
      type: String,
      required: true,
      default: '',
    },
    headCommit: {
      type: String,
      required: true,
      default: '',
    },
    headTreePath: {
      type: String,
      required: false,
      default: '',
    },
    baseCommit: {
      type: String,
      required: false,
      default: '',
    },
    findWarningComponent: {
      type: Boolean,
      required: false,
      default: true,
    },
    initialSelection: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
  },
  emits: ['warnings', 'select'],

  data() {
    const variantId = this.initialSelection?.id || 'default';

    return {
      style: 'style', // This needs to be dynamic for lints to pass
      variants: [] as Variant[],
      currentVariantName: this.initialSelection?.name || '[No Variations]',
      currentVariantId: variantId,
      noVariants: true,
      isLoading: true,
      styleText: this.styleForVariantId(variantId),
    };
  },

  computed: {
    container(): Container {
      return this.untypedContainer as unknown as Container;
    },
  },

  mounted() {
    this.loadVariants();
  },

  methods: {
    /**
     * Load the variants for the given context (either by commit/file or commit range)
     * @returns {Promise<void>}
     */
    async loadVariants() {
      const prjUrl = this.isDiff ?
        AllSpiceJsonLoader.buildPrjRangeUrl(this.container, this.headRepoOwner, this.headRepoName, this.baseCommit, this.headCommit) :
        AllSpiceJsonLoader.buildPrjUrl(this.container, this.headRepoOwner, this.headRepoName, this.headCommit, this.headTreePath);
      const results = await AllSpiceJsonLoader.load<ProjectData>(this.container, prjUrl, null);

      const userVariants = [];
      if (results.headJson?.variants) {
        for (const [variantId, variantName] of Object.entries(results.headJson.variants)) {
          userVariants.push(new Variant(variantId, variantName));
        }
      }

      // display dynamic warnings if applicable
      if (results.headJson?.warnings) {
        this.$emit('warnings', results.headJson.warnings);
        if (this.findWarningComponent) {
          const elem = document.querySelector('.warning-messages');
          if (!elem) return;
          const warningMessages = vueInstance(elem);
          if (!warningMessages) return;
          warningMessages.addWarnings(results.headJson.warnings);
        }
      }

      // add the default variant, sort the user variants
      this.variants = [new Variant('default', '[No Variations]')].concat(userVariants.sort(this.variantCompareAlphaNumeric));

      // if there is only default, disable dropdown
      this.noVariants = this.variants.length === 1;

      // Guard against the component having unmounted during the async load.
      if (!this.$refs.dropdown) return;

      $(this.$refs.dropdown).dropdown({
        useLabels: false,
      });

      // When the user clicks on the dropdown button while the dropdown is open,
      // close the dropdown.
      (this.$refs.dropdown as HTMLElement).addEventListener('click', this.variantClickHandler);

      this.isLoading = false;

      this.applyVariant({name: this.currentVariantName, id: this.currentVariantId});
    },

    /**
     * Handle a variant selection
     * @param event
     */
    variantClickHandler(event: Event) {
      const isButton = event.target === this.$refs.dropdown;
      if (isButton) {
        const $this = $(this.$refs.dropdown);
        if ($this.dropdown('is visible')) {
          $this.dropdown('hide');
        }
      }
    },

    /**
     * Compare two variants by name, sort in descending alphanumeric order
     * @param {Variant} v1
     * @param {Variant} v2
     * @return Number
     */
    variantCompareAlphaNumeric(v1: Variant, v2: Variant) {
      return v1.name.localeCompare(v2.name, 'en', {numeric: true});
    },

    /**
     * @param {Variant} variant
     */
    selectVariant(variant: Variant) {
      // update the dropdown name to have the current variant
      this.currentVariantName = variant.name;
      this.currentVariantId = variant.id;

      this.$emit('select', {name: variant.name, id: variant.id});
      this.applyVariant(variant);
    },

    /**
     * @param {Variant} variant
     */
    applyVariant(variant: Variant) {
      this.styleText = this.styleForVariantId(variant.id);
    },

    /**
     * @param {string} variantId
     * @return string
     */
    styleForVariantId(variantId: string) {
      const styles = [];
      for (const svgSelector of ['svg.schematic_svg', 'svg.schematic']) {
        styles.push(cssForVariant(svgSelector, variantId));
      }
      return styles.join('\n');
    },
  },
});
</script>

<template>
  <div
    ref="dropdown"
    :class="(noVariants || isLoading) ? 'disabled' : ''"
    class="ui dropdown icon scrolling basic button small variant-dropdown"
    :data-variant-id="currentVariantId"
  >
    <component :is="style">{{ styleText }}</component>
    <span>Variant: <strong>{{ currentVariantName }}</strong></span>
    <div class="dropdown-icon">
      <div v-if="isLoading" class="is-loading tw:size-8"/>
      <svg v-else viewBox="0 0 16 16" class="dropdown icon svg octicon-triangle-down dropdown-icon-loaded" width="14" height="14" aria-hidden="true" tabindex="0"><path d="m4.427 7.427 3.396 3.396a.25.25 0 0 0 .354 0l3.396-3.396A.25.25 0 0 0 11.396 7H4.604a.25.25 0 0 0-.177.427z"/></svg>
    </div>
    <div class="ui menu">
      <div class="item variant-select" v-for="variant in variants" :key="variant.id" @click="selectVariant(variant)">
        {{ variant.name }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.dropdown-icon {
  height: 28px;
  width: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
